/**
 * InlineList — liste éditable inline pour un tableau de strings.
 *
 * - En mode PUBLIC : rend chaque item via `renderItem(text, index)`.
 * - En mode ADMIN : chaque item devient éditable en place (InlineText)
 *   avec boutons ↑ ↓ 🗑 au survol, et un bouton "+ Ajouter" en bas de la liste.
 *
 * Usage :
 *   <InlineList
 *     items={essential}
 *     path={`experiences.${id}.essential`}
 *     onSave={(newArray) => saveText(`experiences.${id}.essential`, newArray)}
 *     renderItem={(t) => (
 *       <li className="...">
 *         <span className="dot" />
 *         {t}
 *       </li>
 *     )}
 *     wrapAs="ul"
 *     wrapClassName="space-y-3"
 *   />
 */
import { useState, useEffect, useRef } from "react";
import { ArrowUp, ArrowDown, Trash2, Plus, Pencil, Save, X } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function InlineList({
  items = [],
  path,
  onSave,
  renderItem,
  wrapAs: WrapTag = "ul",
  wrapClassName = "",
  addLabel = "Ajouter une ligne",
  placeholder = "Nouveau texte…",
}) {
  const { user } = useAuth();
  const isAdmin = !!user;
  const [list, setList] = useState(items);
  const [editingIdx, setEditingIdx] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setList(items); }, [items]);
  useEffect(() => { if (editingIdx !== null && inputRef.current) inputRef.current.focus(); }, [editingIdx]);

  const persist = async (next) => {
    setBusy(true);
    setList(next); // optimistic
    try { await onSave(next); } catch (_) { setList(items); } finally { setBusy(false); }
  };
  const moveItem = (from, to) => {
    if (to < 0 || to >= list.length) return;
    const next = list.slice();
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    persist(next);
  };
  const removeItem = (idx) => {
    if (!window.confirm("Supprimer cette ligne ?")) return;
    const next = list.slice(); next.splice(idx, 1); persist(next);
  };
  const saveEdit = () => {
    const next = list.slice(); next[editingIdx] = draft; setEditingIdx(null); persist(next);
  };
  const addItem = () => {
    const next = list.concat([""]); setList(next); setEditingIdx(next.length - 1); setDraft("");
  };

  if (!isAdmin) {
    return (
      <WrapTag className={wrapClassName}>
        {list.map((t, i) => (t ? renderItem(t, i) : null))}
      </WrapTag>
    );
  }

  return (
    <WrapTag className={wrapClassName} data-testid={`inline-list-${path}`}>
      {list.map((t, i) => (
        <li key={`${path}-${i}`} className="relative group/list-item">
          {editingIdx === i ? (
            <div className="flex items-start gap-2 pl-4 pr-2 py-2 bg-white border border-refuge-ruby rounded-md shadow-md">
              <textarea
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); saveEdit(); }
                  if (e.key === "Escape") { setEditingIdx(null); if (!list[i]) removeItem(i); }
                }}
                placeholder={placeholder}
                className="flex-1 p-1 text-sm font-body focus:outline-none resize-none min-h-[36px]"
                data-testid={`inline-list-${path}-input-${i}`}
                rows={1}
              />
              <button onClick={saveEdit} disabled={busy} data-testid={`inline-list-${path}-save-${i}`}
                className="w-7 h-7 rounded-full bg-refuge-ruby text-white flex items-center justify-center hover:bg-refuge-ruby/90 disabled:opacity-40">
                <Save size={12} />
              </button>
              <button onClick={() => { setEditingIdx(null); if (!list[i]) removeItem(i); }}
                className="w-7 h-7 rounded-full border border-refuge-border text-refuge-text flex items-center justify-center hover:bg-refuge-bg">
                <X size={12} />
              </button>
            </div>
          ) : (
            <div className="flex items-start gap-1">
              <div
                className="flex-1 cursor-pointer"
                onClick={() => { setEditingIdx(i); setDraft(t); }}
                style={{ outline: "1px dashed transparent", outlineOffset: 4, borderRadius: 4 }}
                onMouseEnter={(e) => (e.currentTarget.style.outlineColor = "rgba(139,37,48,0.25)")}
                onMouseLeave={(e) => (e.currentTarget.style.outlineColor = "transparent")}
                data-testid={`inline-list-${path}-view-${i}`}
              >
                {renderItem(t, i)}
              </div>
              <div className="flex items-center gap-0.5 opacity-0 group-hover/list-item:opacity-100 transition-opacity">
                <button
                  onClick={(e) => { e.stopPropagation(); setEditingIdx(i); setDraft(t); }}
                  data-testid={`inline-list-${path}-edit-${i}`}
                  className="w-6 h-6 rounded-full bg-refuge-ruby text-white flex items-center justify-center hover:bg-refuge-ruby/90 shadow-sm">
                  <Pencil size={10} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); moveItem(i, i - 1); }}
                  disabled={busy || i === 0}
                  data-testid={`inline-list-${path}-up-${i}`}
                  className="w-6 h-6 rounded-full bg-refuge-text/85 text-white flex items-center justify-center hover:bg-refuge-text disabled:opacity-30 shadow-sm">
                  <ArrowUp size={10} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); moveItem(i, i + 1); }}
                  disabled={busy || i === list.length - 1}
                  data-testid={`inline-list-${path}-down-${i}`}
                  className="w-6 h-6 rounded-full bg-refuge-text/85 text-white flex items-center justify-center hover:bg-refuge-text disabled:opacity-30 shadow-sm">
                  <ArrowDown size={10} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); removeItem(i); }}
                  data-testid={`inline-list-${path}-del-${i}`}
                  className="w-6 h-6 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-700 shadow-sm">
                  <Trash2 size={10} />
                </button>
              </div>
            </div>
          )}
        </li>
      ))}
      <li className="pt-2">
        <button
          onClick={addItem}
          data-testid={`inline-list-${path}-add`}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-dashed border-refuge-ruby/50 text-refuge-ruby text-xs font-body hover:bg-refuge-ruby/10">
          <Plus size={12} /> {addLabel}
        </button>
      </li>
    </WrapTag>
  );
}
